// components/CalendarEvent.jsx
import React, { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";
import { Clock, User } from "../Icons";

const CalendarEvent = memo(({ 
  event, 
  onClick, 
  style, 
  displayInfo,
  isMultiDay = false,
  isMonthView = false,
  eventIndex = 0,
}) => {
  const { t } = useTranslation("global");

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    onClick?.(e);
  }, [onClick]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(e);
    }
  }, [handleClick]);

  const formatTime = (date) => {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const formatEventTime = () => {
    if (isMultiDay && !displayInfo) return t('calendar.multiDay');
    if (displayInfo) return `${displayInfo.displayStart} - ${displayInfo.displayEnd}`;
    return `${formatTime(event.start)} - ${formatTime(event.end)}`;
  };

  const eventClassName = useMemo(() => {
    const classes = [
      styles.event,
      styles[`event--${event.type}`],
      isMultiDay && styles.multiDayEvent,
      isMonthView && styles.monthViewEvent,
    ].filter(Boolean).join(' ');
    
    return classes;
  }, [event.type, isMultiDay, isMonthView]);

  return (
    <div
      className={eventClassName}
      style={style}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`${event.title}, ${formatEventTime()}`}
      data-is-start={displayInfo?.isStartDay}
      data-is-end={displayInfo?.isEndDay}
      data-status={event.extendedProps?.status}
      data-priority={event.extendedProps?.priority}
      data-index={eventIndex}
    >
      <div className={styles.eventContent}>
        <div className={styles.eventHeader}>
          {event.extendedProps?.placeGroup?.color && (
            <div 
              className={styles.eventColorDot}
              style={{ backgroundColor: event.extendedProps.placeGroup.color }}
            />
          )}
          <div className={styles.eventTitle}>
            {event.title}
            {isMultiDay && !isMonthView && (
              <span className={styles.multiDayIndicator}>⟷</span>
            )}
          </div>
        </div>
        
        {!isMonthView && (
          <div className={styles.eventMeta}>
            <div className={styles.eventTime}>
              <Clock size={12} />
              <span>{formatEventTime()}</span>
            </div>
            
            {event.extendedProps?.customer?.firstName && (
              <div className={styles.eventCustomer}>
                <User size={12} />
                <span>{event.extendedProps.customer.firstName}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

CalendarEvent.displayName = "CalendarEvent";

export default CalendarEvent;